/**
 * InsightsSelector - Homepage §9: Three dynamic AI insights
 *
 * Uses the deterministic selectInsights() scorer to surface 3 posts
 * that are fresh, business-aligned, and topic-diverse.
 * Falls back to static featured posts if the scorer returns nothing.
 */
import { Link } from "wouter";
import { ArrowRight, CalendarDays, TrendingUp } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import {
  getHomepageInsightFilters,
  selectInsights,
  type HomepageInsightFilter,
  type ScoredInsight,
} from "@/lib/insightsSelector";
import type { BlogPost } from "@/data/blogPosts";
import { featuredPosts } from "@/data/featuredPosts";
import { trackSectionCtaClick } from "@/lib/analytics";
import { ResponsiveImage } from "@/components/ResponsiveImage";

// ─── Skeleton ────────────────────────────────────────────────────────────────
function SkeletonCard({ delay = 0 }: { delay?: number }) {
  return (
    <div
      className="rounded-sm overflow-hidden bg-[#F7F5F2] animate-pulse"
      style={{ animationDelay: `${delay}ms` }}
    >
      <div className="h-44 bg-[#E8E4DF]" />
      <div className="p-5 space-y-3">
        <div className="h-3 bg-[#E8E4DF] rounded w-1/3" />
        <div className="h-5 bg-[#E8E4DF] rounded w-4/5" />
        <div className="h-5 bg-[#E8E4DF] rounded w-3/5" />
        <div className="h-3 bg-[#E8E4DF] rounded w-1/2 mt-4" />
      </div>
    </div>
  );
}

// ─── Post card ────────────────────────────────────────────────────────────────
interface PostCardProps {
  slug: string;
  title: string;
  date: string;
  tags: string[];
  heroImage?: string;
  excerpt: string;
  isTrending?: boolean;
  pinned?: boolean;
  index: number;
}

function PostCard({ slug, title, date, tags, heroImage, excerpt, isTrending, pinned, index }: PostCardProps) {
  const isBenchmark = slug === "ai-visibility-benchmark-2026-citation-rates-share-of-voice";
  const isNew = (() => {
    const d = new Date(date);
    const now = new Date();
    return (now.getTime() - d.getTime()) / (1000 * 60 * 60 * 24) <= 30;
  })();

  return (
    <Link
      href={`/blog/${slug}/`}
      onClick={() =>
        trackSectionCtaClick({
          section_name: "insights-selector",
          cta_text: title,
          destination: `/blog/${slug}/`,
        })
      }
      className="group flex flex-col rounded-sm overflow-hidden border border-[#E8E4DF] bg-white hover:border-[#FF8C00]/40 hover:shadow-md transition-all duration-200"
      style={{ animationDelay: `${index * 60}ms` }}
    >
      {/* Hero image */}
      <div className="relative h-44 overflow-hidden bg-[#F7F5F2]">
        {heroImage ? (
          <ResponsiveImage
            src={heroImage}
            alt={title}
            width={640}
            height={360}
            sizes="(min-width: 768px) 33vw, calc(100vw - 32px)"
            className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-300"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-[#0F1419] to-[#1A2332]">
            <span className="text-[#FF8C00]/40 text-3xl font-serif">IS</span>
          </div>
        )}
        {/* Badges */}
        <div className="absolute top-3 left-3 flex gap-1.5">
          {isBenchmark ? (
            <span className="text-[10px] font-mono tracking-widest uppercase px-2 py-0.5 rounded-sm bg-[#FF8C00] text-[#0F1419] font-semibold">
              Trending · Benchmark
            </span>
          ) : pinned && (
            <span className="text-[10px] font-mono tracking-widest uppercase px-2 py-0.5 rounded-sm bg-[#FF8C00] text-[#0F1419] font-semibold">
              Featured
            </span>
          )}
          {isTrending && !pinned && (
            <span className="text-[10px] font-mono tracking-widest uppercase px-2 py-0.5 rounded-sm bg-[#0F1419]/80 text-[#FF8C00] flex items-center gap-1">
              <TrendingUp className="w-2.5 h-2.5" aria-hidden="true" />
              Trending
            </span>
          )}
          {isNew && !pinned && !isTrending && (
            <span className="text-[10px] font-mono tracking-widest uppercase px-2 py-0.5 rounded-sm bg-emerald-500 text-white font-semibold">
              New
            </span>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="flex flex-col flex-1 p-5">
        {/* Tags */}
        {!isBenchmark && (
          <div className="flex flex-wrap gap-1.5 mb-3">
            {tags.slice(0, 2).map((tag) => (
              <span
                key={tag}
                className="text-xs px-2 py-0.5 rounded bg-amber-500/10 text-amber-700 font-medium"
              >
                {tag}
              </span>
            ))}
          </div>
        )}

        {/* Title */}
        <h3 className="font-serif text-lg leading-tight text-[#1A1A2E] group-hover:text-[#FF8C00] transition-colors mb-3 line-clamp-2">
          {title}
        </h3>

        {/* Excerpt */}
        <p className={`text-sm text-[#6B7280] leading-relaxed flex-1 ${isBenchmark ? "" : "line-clamp-3"}`}>
          {isBenchmark ? excerpt : `${excerpt.slice(0, 120)}…`}
        </p>

        {/* Meta */}
        <div className="flex items-center justify-between mt-4 pt-3 border-t border-[#E8E4DF]">
          <span className="flex items-center gap-1.5 text-xs text-[#9CA3AF]">
            <CalendarDays className="w-3.5 h-3.5" aria-hidden="true" />
            {new Date(date).toLocaleDateString("en-GB", {
              month: "short",
              day: "numeric",
              year: "numeric",
            })}
          </span>
          <span className="text-xs font-medium text-[#FF8C00] flex items-center gap-1 group-hover:translate-x-0.5 transition-transform">
            Read <ArrowRight className="w-3 h-3" aria-hidden="true" />
          </span>
        </div>
      </div>
    </Link>
  );
}

// ─── Main component ────────────────────────────────────────────────────────────
export default function InsightsSelector() {
  // Dynamic import: load blog data lazily so the 3MB content strings
  // stay out of the initial JS bundle (critical for PageSpeed).
  const [allPosts, setAllPosts] = useState<BlogPost[]>(featuredPosts as BlogPost[]);
  const [activeFilter, setActiveFilter] = useState<HomepageInsightFilter>("All");

  useEffect(() => {
    let cancelled = false;
    import("@/data/blogPosts").then(({ blogPosts }) => {
      if (cancelled) return;
      setAllPosts(blogPosts);
    });
    return () => { cancelled = true; };
  }, []);

  const filters = useMemo(() => getHomepageInsightFilters(allPosts), [allPosts]);
  const insights = useMemo(() => {
    const postsForTopic =
      activeFilter === "All"
        ? allPosts
        : allPosts.filter((post) => post.tags?.includes(activeFilter));
    return selectInsights(postsForTopic);
  }, [activeFilter, allPosts]);

  // Identify the highest-scored post as "trending"
  const trendingSlug = insights.length > 0 ? insights[0].post.slug : null;

  // The curated fallback is rendered immediately for SSR and first paint; the full registry then refreshes the same selector client-side.
  return (
    <section
      className="pt-12 sm:pt-16 lg:pt-28 pb-12 sm:pb-14 lg:pb-16 bg-white border-t border-[#E8E4DF]"
      id="insights"
      aria-labelledby="insights-heading"
    >
      <div className="container max-w-6xl mx-auto px-4">
        {/* Section Header */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-10">
          <div>
            <span className="text-mono text-[#FF8C00] text-xs tracking-widest uppercase mb-3 block">
              Insights &amp; Analysis
            </span>
            <h2
              id="insights-heading"
              className="font-serif text-3xl md:text-4xl tracking-tight text-[#1A1A2E]"
            >
              AI Marketing &amp; AEO Intelligence
            </h2>
            <p className="text-[#6B7280] mt-3 max-w-xl text-base leading-relaxed">
              Expert analysis on AI search visibility, agentic AI for enterprise, and the
              strategies that win citations in Google AI Overviews, ChatGPT, and Perplexity.
            </p>
          </div>
          <Link
            href="/blog/"
            onClick={() =>
              trackSectionCtaClick({
                section_name: "insights-selector",
                cta_text: "View all articles",
                destination: "/blog/",
              })
            }
            className="flex items-center gap-2 text-[#FF8C00] font-medium hover:text-amber-600 transition-colors whitespace-nowrap"
          >
            View all articles <ArrowRight className="w-4 h-4" aria-hidden="true" />
          </Link>
        </div>

        <div className="mb-7 sm:mb-8">
          <p id="insight-topic-filter-label" className="font-mono text-[11px] uppercase tracking-[0.16em] text-[#6B7280] mb-3">
            Browse the latest analysis by topic
          </p>
          <div
            className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1 [scrollbar-width:thin]"
            role="group"
            aria-labelledby="insight-topic-filter-label"
          >
            {filters.map((filter) => {
              const isActive = filter === activeFilter;
              return (
                <button
                  key={filter}
                  type="button"
                  aria-pressed={isActive}
                  onClick={() => setActiveFilter(filter)}
                  className={`shrink-0 min-h-11 rounded-full border px-4 py-2 text-sm font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#FF8C00] focus-visible:ring-offset-2 focus-visible:ring-offset-white ${
                    isActive
                      ? "border-[#FF8C00] bg-[#FF8C00] text-[#0F1419]"
                      : "border-[#D5CEC5] bg-white text-[#4B5563] hover:border-[#FF8C00]/60 hover:text-[#1A1A2E]"
                  }`}
                >
                  {filter}
                </button>
              );
            })}
          </div>
          <p className="sr-only" aria-live="polite">
            Showing {activeFilter === "All" ? "all trending topics" : activeFilter} insights.
          </p>
        </div>

        {/* Posts Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 lg:gap-6">
          {insights.length === 0
            ? [0, 1, 2].map((i) => <SkeletonCard key={i} delay={i * 80} />)
            : insights.map((insight, index) => (
                <PostCard
                  key={insight.post.slug}
                  slug={insight.post.slug}
                  title={insight.post.title}
                  date={insight.post.date}
                  tags={insight.post.tags}
                  heroImage={insight.post.heroImage}
                  excerpt={insight.post.excerpt}
                  isTrending={insight.post.slug === trendingSlug && !insight.pinned}
                  pinned={insight.pinned}
                  index={index}
                />
              ))}
        </div>
      </div>
    </section>
  );
}
